<template>
  <v-bottom-sheet
    v-model="internalIsOpen"
    :max-width="maxWidth"
    :persistent="persistent"
    scrollable
    transition="bottom-sheet-transition"
    class="custom-bottom-sheet"
  >
    <v-card class="bottom-sheet-card">
      <!-- Fixed Close Button (top-right) -->
      <v-btn icon size="small" class="fixed-close-btn" @click="closeModal">
        <v-icon>mdi-close</v-icon>
      </v-btn>

      <!-- Scrollable Content Area -->
      <v-card-text
        ref="cardContent"
        class="sheet-content"
        @scroll="handleScroll"
      >
        <!-- Fading Image -->
        <div
          v-if="imageUrl"
          class="image-section"
          :style="{ opacity: imageOpacity }"
        >
          <v-img
            :src="imageUrl"
            :height="imageHeight"
            cover
            class="sheet-image"
          >
            <template v-slot:placeholder>
              <v-row class="fill-height ma-0" align="center" justify="center">
                <v-progress-circular indeterminate color="primary" />
              </v-row>
            </template>
          </v-img>
        </div>

        <!-- Sticky Header Title -->
        <div
          v-if="title"
          :class="['sticky-title', { visible: !imageUrl || isScrolled }]"
        >
          <v-app-bar-title>
            <span class="modal-title-bar" v-text="title" />
          </v-app-bar-title>
        </div>

        <!-- Main Content -->
        <div class="main-content">
          <!-- Optional title under image -->
          <div v-if="imageUrl && !isScrolled && title" class="content-title">
            <v-app-bar-title>
              <span class="modal-title-bar" v-text="title" />
            </v-app-bar-title>
          </div>
          <slot></slot>
        </div>
      </v-card-text>

      <!-- Footer -->
      <v-card-actions v-if="$slots.footer" class="sheet-footer">
        <slot name="footer" />
      </v-card-actions>
    </v-card>
  </v-bottom-sheet>
</template>

<script>
export default {
  name: "BottomSheetModal",
  props: {
    modelValue: {
      type: Boolean,
      default: undefined,
    },
    isOpen: {
      type: Boolean,
      default: false,
    },
    title: {
      type: String,
      default: "",
    },
    imageUrl: {
      type: String,
      default: null,
    },
    imageHeight: {
      type: [String, Number],
      default: 200,
    },
    maxWidth: {
      type: [String, Number],
      default: 600,
    },
    persistent: {
      type: Boolean,
      default: false,
    },
    scrollThreshold: {
      type: Number,
      default: 20,
    },
  },
  emits: ["update:modelValue", "update:isOpen", "close"],
  data() {
    return {
      isScrolled: false,
      imageOpacity: 1,
    };
  },
  computed: {
    internalIsOpen: {
      get() {
        return this.isOpen ?? this.modelValue;
      },
      set(value) {
        this.$emit("update:modelValue", value);
        this.$emit("update:isOpen", value);
        if (!value) this.$emit("close");
      },
    },
  },
  watch: {
    internalIsOpen(val) {
      if (val) {
        this.isScrolled = false;
        this.imageOpacity = 1;
        this.$nextTick(() => {
          if (this.$refs.cardContent) {
            this.$refs.cardContent.scrollTop = 0;
          }
        });
      }
    },
  },
  methods: {
    closeModal() {
      this.internalIsOpen = false;
    },
    handleScroll(event) {
      const scrollTop = event.target.scrollTop;
      const threshold = parseFloat(this.imageHeight) * 0.7;
      this.imageOpacity = 1 - Math.min(scrollTop / threshold, 1);
      this.isScrolled = scrollTop > this.imageHeight - 60;
    },
  },
};
</script>

<style scoped>
.custom-bottom-sheet {
  position: relative;
}

.bottom-sheet-card {
  border-top-left-radius: 16px !important;
  border-top-right-radius: 16px !important;
  max-height: 90vh;
  display: flex;
  flex-direction: column;
  overflow: hidden; /* Preserve rounded corners */
  position: relative;
}

.sheet-content {
  flex: 1;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  padding: 0 !important;
  position: relative;
}

.image-section {
  transition: opacity 0.3s ease;
}

.sheet-image {
  width: 100%;
  border-top-left-radius: 16px;
  border-top-right-radius: 16px;
}

.fixed-close-btn {
  position: absolute !important;
  top: 12px;
  right: 12px;
  z-index: 20;
  background-color: #fff !important;
}

.sticky-title {
  position: sticky;
  top: 0;
  z-index: 10;
  background-color: white;
  border-bottom: 1px solid rgba(0, 0, 0, 0.12);
  padding: 14px 16px;
  display: none;
  transition: opacity 0.3s ease;
}

.sticky-title.visible {
  display: block;
}

.main-content {
  padding: 16px;
}

.content-title {
  margin-bottom: 16px;
}

.sheet-footer {
  border-top: 1px solid rgba(0, 0, 0, 0.12);
  padding: 16px !important;
}
</style>
